// The server refuses a bad add-on line with 400 { type: 'RENTAL_INVALID', lines: [{ addonId, name, reason }] }.
// The same shape is used for the old equipment, so only lines that name an add-on (or one of the add-on reasons)
// are ours; anything else is left for the code that handled it before.

type AddonErrorLine = {addonId?: string; name?: string | null; reason?: string}

export type AddonErrorLike = {response?: {status?: number; data?: unknown}}

type RentalInvalidBody = {type?: string; lines?: AddonErrorLine[]}

const ADDON_REASONS = [
  'ADDON_UNKNOWN',
  'ADDON_NOT_OFFERED',
  'ADDON_INACTIVE',
  'ADDON_PER_BOOKING_FIRST_PERSON',
  'INVALID_QUANTITY',
  'QUANTITY_ABOVE_MAX',
]

const isAddonLine = (line: AddonErrorLine) =>
  Boolean(line.addonId) ||
  (line.reason ? line.reason.startsWith('ADDON_') || line.reason === 'QUANTITY_ABOVE_MAX' : false)

export const getAddonErrorLines = (error?: AddonErrorLike | null): AddonErrorLine[] => {
  const response = error?.response
  const body = response?.data as RentalInvalidBody | undefined | null
  if (response?.status !== 400 || body?.type !== 'RENTAL_INVALID' || !Array.isArray(body.lines)) {
    return []
  }
  return body.lines.filter((line) => isAddonLine(line) && (!line.reason || ADDON_REASONS.includes(line.reason)))
}

export const isAddonInvalidError = (error?: AddonErrorLike | null): boolean => getAddonErrorLines(error).length > 0

type Translate = (key: string, options?: Record<string, unknown>) => string

const sentenceFor = (line: AddonErrorLine, translate: Translate): string => {
  const name = line.name || translate('addon_error_this_addon')
  switch (line.reason) {
    case 'QUANTITY_ABOVE_MAX':
      return translate('addon_error_above_max', {name})
    case 'ADDON_PER_BOOKING_FIRST_PERSON':
      return translate('addon_error_first_person', {name})
    case 'INVALID_QUANTITY':
      return translate('addon_error_quantity', {name})
    default:
      return translate('addon_error_unavailable', {name})
  }
}

// One plain sentence for the first bad line, or null when the error is not about add-ons.
export const getAddonErrorMessage = (error: AddonErrorLike | null | undefined, translate: Translate): string | null => {
  const [first] = getAddonErrorLines(error)
  return first ? sentenceFor(first, translate) : null
}
